import { playbookCmsDetailOptions } from '@doneverse/data/hooks'
import type { Playbook } from '@doneverse/shared'
import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import {
  CmsEditorHeaderActions,
  CmsEditorNotFound,
  CmsEditorShell,
  CmsEditorSkeleton,
} from './CmsEditorShell'
import { CmsEditorTabs, type CmsEditorTab } from './CmsEditorTabs'
import { PublishStatusBadge } from './PublishStatusBadge'

/**
 * The playbook editor's chrome: loads the playbook by slug, then hands it to
 * the tab body inside the shared `CmsEditorShell`.
 *
 * The Doer brief tab is the only conditional one — a brief for a Doing Team
 * only exists on `doing_team` playbooks.
 */
const TABS: readonly (CmsEditorTab & { doingTeamOnly?: boolean })[] = [
  { to: '/admin/playbooks/$slug', label: 'Details', exact: true },
  { to: '/admin/playbooks/$slug/founder-page', label: 'Founder page' },
  { to: '/admin/playbooks/$slug/doer-page', label: 'Doer page' },
  { to: '/admin/playbooks/$slug/media', label: 'Media' },
  { to: '/admin/playbooks/$slug/schema', label: 'Brief' },
  { to: '/admin/playbooks/$slug/doer-schema', label: 'Doer brief', doingTeamOnly: true },
  { to: '/admin/playbooks/$slug/publish', label: 'Publish' },
]

/** Which tabs this playbook shows. */
function tabsFor(type: Playbook['type']) {
  return TABS.filter((tab) => !tab.doingTeamOnly || type === 'doing_team')
}

/**
 * The strip on its own, for the two brief tabs: they are full-height pages that
 * hand their scroll to SchemaEditor, so they render the strip themselves rather
 * than sitting inside the shell's card.
 */
export function PlaybookEditorTabs({ slug, type }: { slug: string; type: Playbook['type'] }) {
  return <CmsEditorTabs tabs={tabsFor(type)} params={{ slug }} label="Playbook editor sections" />
}

export function PlaybookEditorShell({
  subtitle,
  children,
}: {
  subtitle: string
  children: (playbook: Playbook) => ReactNode
}) {
  const { slug } = useParams({ strict: false })
  const { data: playbook, isLoading, isError } = useQuery(playbookCmsDetailOptions(slug ?? ''))

  if (isLoading) return <PlaybookEditorSkeleton />

  if (isError || !playbook) {
    return (
      <CmsEditorNotFound
        title="Playbook"
        message="That playbook does not exist, or has been deleted."
        backTo="/admin/playbooks"
        backLabel="Back to Playbooks"
      />
    )
  }

  return (
    <CmsEditorShell
      title={playbook.name}
      badge={<PublishStatusBadge status={playbook.status} />}
      subtitle={subtitle}
      tabs={tabsFor(playbook.type)}
      tabsLabel="Playbook editor sections"
      params={{ slug: playbook.slug }}
      actions={
        <CmsEditorHeaderActions
          live={
            playbook.status === 'published'
              ? { to: '/playbooks/$slug', params: { slug: playbook.slug } }
              : null
          }
          backTo="/admin/playbooks"
          backLabel="All playbooks"
        />
      }
    >
      {children(playbook)}
    </CmsEditorShell>
  )
}

export function PlaybookEditorSkeleton() {
  return <CmsEditorSkeleton />
}
